// @ts-check
import { useState } from 'react';
import { useToast } from '../../context/ToastContext';
import { reportBulkResult, runBulk } from '../../services/bulkService';
import { STOCK_ADJUST_REASONS } from '../../utils/stockReasons';
import Modal from '../ui/Modal';

const INPUT = 'w-full border rounded-lg px-3 py-2 text-sm';

/**
 * Set or shift the stock of every selected variant at once. The reason is required and lands in each
 * variant's stock history, exactly like the single Adjust button.
 * @param {{ ids: string[], onClose: () => void, onDone: () => void }} props
 */
const BulkStockDialog = ({ ids, onClose, onDone }) => {
  const toast = useToast();
  const [mode, setMode] = useState('set');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const whole = quantity !== '' && Number.isInteger(Number(quantity));
  const valid = whole && reason !== '' && (mode === 'set' ? Number(quantity) >= 0 : Number(quantity) !== 0);

  const apply = async () => {
    setBusy(true);
    try {
      const result = await runBulk('/api/admin/inventory/bulk', {
        ids,
        action: 'adjust',
        params: { mode, quantity: Number(quantity), reason, ...(notes.trim() && { notes: notes.trim() }) },
      });
      reportBulkResult(toast, result, 'adjusted', 'variant');
      onDone();
    } catch (error) {
      toast.error(error.message || 'Failed to adjust stock');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={busy ? undefined : onClose} maxWidth="md" fullHeight={false}>
      <div className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">
          Adjust stock <span className="text-sm font-normal text-muted-foreground">({ids.length} selected)</span>
        </h2>
        <div className="flex gap-2">
          <select aria-label="Mode" className={INPUT} value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="set">Set to</option>
            <option value="add">Add (negative removes)</option>
          </select>
          <input
            aria-label="Quantity"
            className={INPUT}
            type="number"
            step="1"
            min={mode === 'set' ? 0 : undefined}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </div>
        <select aria-label="Reason" className={INPUT} value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="">Select reason *</option>
          {STOCK_ADJUST_REASONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <textarea
          aria-label="Notes"
          className={INPUT}
          rows={2}
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Variants that would go below zero, or already have this stock, are skipped and listed afterwards.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-sm border rounded-lg">
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!valid || busy}
            className="px-4 py-2 text-sm text-primary-foreground bg-primary rounded-lg disabled:opacity-50"
          >
            {busy ? 'Working...' : 'Apply'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkStockDialog;
